<template>
  <footer class="bg-slate-900 border-t border-slate-800 text-white pt-16 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <!-- Brand -->
        <div class="col-span-1 md:col-span-1">
          <div class="flex items-center gap-2 mb-6">
            <img src="/img/logo.png" alt="EM Contracting Corp Logo" class="h-16 w-auto object-contain">
          </div>
          <p class="text-gray-400 text-sm leading-relaxed mb-6">
            {{ siteInfo.site_tagline || 'Building more than spaces, we build trust and deliver confidence.' }}
          </p>
        </div>

        <!-- Links -->
        <div>
          <h3 class="text-lg font-semibold mb-6">Company</h3>
          <ul class="space-y-4">
            <li><router-link to="/about" class="text-gray-400 hover:text-amber-500 transition-colors text-sm">About Us</router-link></li>
            <li><router-link to="/services" class="text-gray-400 hover:text-amber-500 transition-colors text-sm">Services</router-link></li>
            <li><router-link to="/projects" class="text-gray-400 hover:text-amber-500 transition-colors text-sm">Projects</router-link></li>
            <li><router-link to="/contact" class="text-gray-400 hover:text-amber-500 transition-colors text-sm">Contact</router-link></li>
          </ul>
        </div>

        <!-- Contact -->
        <div class="col-span-1 md:col-span-2">
          <h3 class="text-lg font-semibold mb-6">Contact</h3>
          <ul class="space-y-4 text-sm text-gray-400">
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              <span v-if="contactInfo.contact_address" v-html="contactInfo.contact_address.replace(/\n/g, '<br>')"></span>
              <span v-else>156 E Milton St<br>Freeport, NY 11520, EE. UU.</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
              <span>{{ contactInfo.contact_phone || '+1 (516) 233-5434' }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              <span>{{ contactInfo.contact_email || 'estimate@emcontractingcorp.com' }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p class="text-sm text-gray-500">
          &copy; {{ new Date().getFullYear() }} EM CONTRACTING CORP. All rights reserved.
        </p>
        <div class="flex space-x-6">
          <a v-if="socialLinks.social_facebook && socialLinks.social_facebook.trim() !== ''" :href="socialLinks.social_facebook" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">Facebook</span>
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path fill-rule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clip-rule="evenodd" />
            </svg>
          </a>
          <a v-if="socialLinks.social_instagram && socialLinks.social_instagram.trim() !== ''" :href="socialLinks.social_instagram" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">Instagram</span>
            <i class="fab fa-instagram h-5 w-5"></i>
          </a>
          <a v-if="socialLinks.social_tiktok && socialLinks.social_tiktok.trim() !== ''" :href="socialLinks.social_tiktok" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">TikTok</span>
            <i class="fab fa-tiktok h-5 w-5"></i>
          </a>
          <a v-if="socialLinks.social_linkedin && socialLinks.social_linkedin.trim() !== ''" :href="socialLinks.social_linkedin" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">LinkedIn</span>
            <i class="fab fa-linkedin h-5 w-5"></i>
          </a>
          <a v-if="socialLinks.social_youtube && socialLinks.social_youtube.trim() !== ''" :href="socialLinks.social_youtube" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">YouTube</span>
            <i class="fab fa-youtube h-5 w-5"></i>
          </a>
          <a v-if="socialLinks.social_x && socialLinks.social_x.trim() !== ''" :href="socialLinks.social_x" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">X (Twitter)</span>
            <i class="fab fa-x-twitter h-5 w-5"></i>
          </a>
          <a v-if="socialLinks.social_whatsapp && socialLinks.social_whatsapp.trim() !== ''" :href="`https://wa.me/${socialLinks.social_whatsapp.replace(/\D/g,'')}`" target="_blank" class="text-gray-500 hover:text-amber-500 transition-colors">
            <span class="sr-only">WhatsApp</span>
            <i class="fab fa-whatsapp h-5 w-5"></i>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Footer',
  computed: {
    ...mapGetters('settings', ['contactInfo', 'socialLinks', 'siteInfo'])
  }
}
</script>
